import { useState } from 'react';
import useHelpTarget from '../hooks/useHelpTarget';

/* Collapsed-line copy: plain-language activity, not snake_case internals.
   Raw tool names (and args/results) stay available inside the expanded
   entries for power users; the summary groups calls into human verbs. */
const TOOL_VERB_RULES = [
  // First: "artifact" is Arc's internal word for Saved work, and
  // `update_artifact` would otherwise match the /date/ rule below.
  [/get_artifacts/, 'checked saved work'],
  [/artifact/, 'saved work for later'],
  [/search|find|lookup|query|locate/, 'searched the case'],
  [/summary|excerpt|read|page|chunk|document/, 'read documents'],
  [/individual|entit|witness|person|people|roster/, 'looked up people'],
  [/chronolog|timeline|event|date/, 'checked the timeline'],
  [/note/, 'kept working notes'],
  [/followup|suggest/, 'prepared follow-ups'],
  [/cite|citation|verify|ground/, 'checked citations'],
];
function describeTools(trace) {
  const verbs = [];
  for (const t of trace) {
    const name = (t.tool_name || t.name || '').toLowerCase();
    const rule = TOOL_VERB_RULES.find(([re]) => re.test(name));
    const verb = rule ? rule[1] : name.replace(/_/g, ' ') || 'worked';
    if (!verbs.includes(verb)) verbs.push(verb);
  }
  const shown = verbs.slice(0, 4);
  const text = shown.join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Expandable tool-call trace for an assistant message. */
export default function ToolTrace({ trace }) {
  const [open, setOpen] = useState(false);
  const helpRef = useHelpTarget({
    slug: 'what-arc-shows-while-thinking',
    label: 'Tool trace',
    question: 'What does this tool trace tell me about how Arc answered?',
  });

  if (!trace?.length) return null;

  const summary = `${describeTools(trace)} — ${trace.length} step${trace.length > 1 ? 's' : ''}`;

  return (
    <div className="tool-trace" ref={helpRef}>
      <button className="tool-trace-toggle btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>
        {open ? '▾' : '▸'} {summary}
      </button>

      {open && (
        <div className="tool-trace-details">
          {trace.map((t, i) => (
            <ToolCallEntry key={i} call={t} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function ToolCallEntry({ call, index }) {
  const [expanded, setExpanded] = useState(false);
  const name = call.tool_name || call.name || `Step ${index + 1}`;
  const hasArgs = call.args && Object.keys(call.args).length > 0;
  const hasResult = call.result != null;

  return (
    <div className="tool-call-entry">
      <button className="tool-call-header btn-ghost btn-sm" onClick={() => setExpanded(e => !e)}>
        <span className="tool-call-name">🔧 {name}</span>
        {call.duration_ms != null && (
          <span className="tool-call-duration">{(call.duration_ms / 1000).toFixed(1)}s</span>
        )}
      </button>

      {expanded && (
        <div className="tool-call-body">
          {hasArgs && (
            <div className="tool-call-section">
              <div className="tool-call-label">Arguments</div>
              <pre className="tool-call-code">{JSON.stringify(call.args, null, 2)}</pre>
            </div>
          )}
          {hasResult && (
            <div className="tool-call-section">
              <div className="tool-call-label">Result</div>
              <pre className="tool-call-code">
                {typeof call.result === 'string' ? call.result : JSON.stringify(call.result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
